import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { AdminLayout } from "./AdminDashboard";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { 
  Facebook, Twitter, Instagram, Linkedin, CheckCircle, XCircle,
  Settings, Eye, EyeOff, Save
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const PLATFORMS = [
  { 
    id: "facebook", 
    name: "Facebook", 
    icon: Facebook, 
    color: "bg-blue-600",
    fields: [
      { key: "page_id", label: "Page ID", type: "text" },
      { key: "access_token", label: "Access Token", type: "password" }
    ],
    instructions: "Get your Page Access Token from Facebook Developer Console"
  },
  { 
    id: "twitter", 
    name: "Twitter/X", 
    icon: Twitter, 
    color: "bg-black",
    fields: [
      { key: "api_key", label: "API Key", type: "text" },
      { key: "api_secret", label: "API Secret", type: "password" },
      { key: "access_token", label: "Access Token", type: "password" },
      { key: "access_secret", label: "Access Token Secret", type: "password" }
    ],
    instructions: "Get credentials from Twitter Developer Portal"
  },
  { 
    id: "instagram", 
    name: "Instagram", 
    icon: Instagram, 
    color: "bg-gradient-to-br from-purple-600 to-pink-500",
    fields: [
      { key: "account_id", label: "Business Account ID", type: "text" },
      { key: "access_token", label: "Access Token", type: "password" }
    ],
    instructions: "Requires Facebook Business integration"
  },
  { 
    id: "linkedin", 
    name: "LinkedIn", 
    icon: Linkedin, 
    color: "bg-blue-700",
    fields: [
      { key: "organization_id", label: "Organization ID", type: "text" },
      { key: "access_token", label: "Access Token", type: "password" }
    ],
    instructions: "Get credentials from LinkedIn Developer Portal"
  }
];

export default function AdminSocialMedia() {
  const [credentials, setCredentials] = useState({});
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [formData, setFormData] = useState({});
  const [showPasswords, setShowPasswords] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchCredentials();
  }, []);

  const fetchCredentials = async () => {
    try {
      const response = await axios.get(`${API}/admin/social-credentials`, { withCredentials: true });
      setCredentials(response.data);
    } catch (error) {
      console.error("Error fetching credentials:", error);
    } finally {
      setLoading(false);
    }
  };

  const openConfigDialog = (platform) => {
    setSelectedPlatform(platform);
    setFormData({});
    setShowPasswords({});
    setDialogOpen(true);
  };

  const saveCredentials = async () => {
    if (!selectedPlatform) return;
    
    setSaving(true);
    try {
      await axios.put(`${API}/admin/social-credentials`, {
        platform: selectedPlatform.id,
        credentials: formData
      }, { withCredentials: true });
      
      toast.success(`${selectedPlatform.name} credentials saved!`);
      setDialogOpen(false);
      fetchCredentials();
    } catch (error) {
      console.error("Error saving credentials:", error);
      toast.error("Failed to save credentials");
    } finally {
      setSaving(false);
    }
  };

  const togglePassword = (key) => {
    setShowPasswords({ ...showPasswords, [key]: !showPasswords[key] });
  };

  return (
    <AdminLayout currentPage="Social Media">
      <div className="p-8" data-testid="admin-social-media">
        <div className="mb-8">
          <h1 className="text-heading-2 text-deep-blue">Social Media Integration</h1>
          <p className="text-slate-600">Connect your social accounts to auto-share blog posts</p>
        </div>

        {/* Platform Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {PLATFORMS.map(platform => {
            const Icon = platform.icon;
            const isConfigured = credentials[platform.id]?.configured;
            
            return (
              <Card key={platform.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-14 h-14 ${platform.color} rounded-xl flex items-center justify-center`}>
                        <Icon className="w-7 h-7 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-deep-blue">{platform.name}</h3>
                        <div className="flex items-center gap-2 mt-1">
                          {isConfigured ? (
                            <>
                              <CheckCircle className="w-4 h-4 text-green-500" />
                              <span className="text-sm text-green-600">Connected</span>
                            </>
                          ) : (
                            <>
                              <XCircle className="w-4 h-4 text-slate-400" />
                              <span className="text-sm text-slate-500">Not configured</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <Button 
                      variant={isConfigured ? "outline" : "default"}
                      onClick={() => openConfigDialog(platform)}
                      data-testid={`config-${platform.id}`}
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      {isConfigured ? "Update" : "Configure"}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Instructions Card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-deep-blue">How Auto-Sharing Works</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <p className="text-slate-600">
                Once you configure your social media accounts, you can automatically share blog posts to all connected platforms.
              </p>
              <ol className="list-decimal list-inside space-y-2 text-slate-600">
                <li>Configure credentials for each platform you want to use</li>
                <li>When publishing a blog post, click "Share to Social Media"</li>
                <li>Select which platforms to share to</li>
                <li>The post will be queued and shared automatically</li>
              </ol>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4">
                <p className="text-sm text-yellow-800">
                  <strong>Note:</strong> Credentials are encrypted and stored securely. 
                  You'll need API access from each platform's developer portal.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Configuration Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3">
                {selectedPlatform && (
                  <>
                    <div className={`w-10 h-10 ${selectedPlatform.color} rounded-lg flex items-center justify-center`}>
                      <selectedPlatform.icon className="w-5 h-5 text-white" />
                    </div>
                    Configure {selectedPlatform.name}
                  </>
                )}
              </DialogTitle>
              <DialogDescription>
                {selectedPlatform?.instructions}
              </DialogDescription>
            </DialogHeader>

            {selectedPlatform && (
              <div className="space-y-4 mt-4">
                {selectedPlatform.fields.map(field => (
                  <div key={field.key}>
                    <Label htmlFor={field.key}>{field.label}</Label>
                    <div className="relative mt-1">
                      <Input
                        id={field.key}
                        type={field.type === "password" && !showPasswords[field.key] ? "password" : "text"}
                        value={formData[field.key] || ""}
                        onChange={(e) => setFormData({...formData, [field.key]: e.target.value})}
                        placeholder={`Enter ${field.label.toLowerCase()}`}
                        className="pr-10"
                      />
                      {field.type === "password" && (
                        <button
                          type="button"
                          onClick={() => togglePassword(field.key)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          {showPasswords[field.key] ? (
                            <EyeOff className="w-4 h-4" />
                          ) : (
                            <Eye className="w-4 h-4" />
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                ))}

                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={saveCredentials} disabled={saving} className="btn-primary">
                    <Save className="w-4 h-4 mr-2" />
                    {saving ? "Saving..." : "Save Credentials"}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
